import { BaseAPI } from '../BaseApi';
import { eAPITags } from '../eAPITags';
import { UUID } from '../../types/shared/UUID';
import { eClientContactActionType } from '../../types/contacts/eClientContactActionType';
import {
  convertFromIDefaultContactRequestArrayToIDefaultContactDataArray,
  IDefaultContactData,
  IDefaultContactRequest,
} from '../../types/clients/IDefaultContact';
import {
  convertFromICreateDefaultContactDataToICreateDefaultContactRequest,
  IAddEditDefaultContactData,
  IAffectedOrders,
  ICalculateImpactRequest,
  ICalculateImpactResponse,
} from '../../types/clients/CreateDefaultContact';
import {
  convertFromClientUserDataToClientUserResponse,
  convertFromClientUserResponseArrayToClientUserDataArray,
  convertFromClientUserResponseToClientUserData,
  IClientUserData,
  IClientUserResponse,
} from '../../types/clients';

interface AddClientUserRequest {
  clientId: UUID;
  contact: IClientUserData;
}

interface EditClientUserRequest {
  clientId: UUID;
  contact: IClientUserData;
}

interface ActionClientContactRequest {
  clientId: UUID;
  contactId: UUID;
  actionType: eClientContactActionType;
}

interface AddDefaultContactRequest {
  clientId: UUID;
  contact: IAddEditDefaultContactData;
}

interface UpdateDefaultContactRequest {
  clientId: UUID;
  contact: IAddEditDefaultContactData;
}

interface DeleteDefaultContactRequest {
  clientId: UUID;
  contact: IAddEditDefaultContactData;
}

interface AffectedOrdersRequest {
  clientId: UUID;
  contact: IAddEditDefaultContactData;
}

export const ClientUsersAPI = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    // CLIENT USERS
    listClientUsers: build.query<IClientUserData[], UUID>({
      query: (clientId) => `/timber/api/internal/client_companies/${clientId}/client_users`,
      transformResponse: (response: IClientUserResponse[]) =>
        convertFromClientUserResponseArrayToClientUserDataArray(response),
      providesTags: (result, error, clientId) => [{ type: eAPITags.ClientUsers, id: clientId }],
    }),

    addClientUser: build.mutation<IClientUserData, AddClientUserRequest>({
      query: ({ clientId, contact }) => ({
        url: `/timber/api/internal/client_companies/${clientId}/client_users`,
        method: 'POST',
        body: convertFromClientUserDataToClientUserResponse(contact),
      }),
      transformResponse: (response: IClientUserResponse) =>
        convertFromClientUserResponseToClientUserData(response),
      invalidatesTags: (result, error, { clientId }) => [{ type: eAPITags.ClientUsers, id: clientId }],
    }),

    editClientContacts: build.mutation<IClientUserData, EditClientUserRequest>({
      query: ({ clientId, contact }) => ({
        url: `/timber/api/internal/client_companies/${clientId}/client_users/${contact.id}`,
        method: 'PUT',
        body: convertFromClientUserDataToClientUserResponse(contact),
      }),
      transformResponse: (response: IClientUserResponse) =>
        convertFromClientUserResponseToClientUserData(response),
      invalidatesTags: (result, error, { clientId }) => [{ type: eAPITags.ClientUsers, id: clientId }],
    }),

    actionClientContacts: build.mutation<void, ActionClientContactRequest>({
      query: ({ clientId, contactId, actionType }) => ({
        url: `/timber/api/internal/client_companies/${clientId}/client_users/${contactId}/${actionType.toLowerCase()}`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: eAPITags.ClientUsers, id: clientId }],
    }),

    // DEFAULT CONTACTS
    listDefaultContacts: build.query<IDefaultContactData[], UUID>({
      query: (clientId) => `/timber/api/internal/clients/${clientId}/default-contacts`,
      transformResponse: (response: IDefaultContactRequest[]) =>
        convertFromIDefaultContactRequestArrayToIDefaultContactDataArray(response),
      providesTags: (result, error, clientId) => [{ type: eAPITags.ClientDefaultContacts, id: clientId }],
    }),

    addDefaultContact: build.mutation<IDefaultContactData, AddDefaultContactRequest>({
      query: ({ clientId, contact }) => ({
        url: `/timber/api/internal/clients/${clientId}/default-contacts`,
        method: 'POST',
        body: convertFromICreateDefaultContactDataToICreateDefaultContactRequest(contact, clientId),
        params: {
          updateOrders: contact.updateOrders,
          statusLowerRange: contact.statusLowerRange,
          statusUpperRange: contact.statusUpperRange || contact.statusLowerRange,
        },
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: eAPITags.ClientDefaultContacts, id: clientId }],
    }),

    updateDefaultContact: build.mutation<IDefaultContactData, UpdateDefaultContactRequest>({
      query: ({ clientId, contact }) => ({
        url: `/timber/api/internal/clients/${clientId}/default-contacts/${contact.id}`,
        method: 'PATCH',
        body: convertFromICreateDefaultContactDataToICreateDefaultContactRequest(contact, clientId),
        params: {
          updateOrders: contact.updateOrders,
          statusLowerRange: contact.statusLowerRange,
          statusUpperRange: contact.statusUpperRange || contact.statusLowerRange,
        },
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: eAPITags.ClientDefaultContacts, id: clientId }],
    }),

    deleteDefaultContact: build.mutation<IDefaultContactData, DeleteDefaultContactRequest>({
      query: ({ clientId, contact }) => ({
        url: `/timber/api/internal/clients/${clientId}/default-contacts/${contact.id}`,
        method: 'DELETE',
        params: {
          updateOrders: contact.updateOrders,
          statusLowerRange: contact.statusLowerRange,
          statusUpperRange: contact.statusUpperRange || contact.statusLowerRange,
        },
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: eAPITags.ClientDefaultContacts, id: clientId }],
    }),

    affectedOrdersForDefaultContact: build.mutation<IAffectedOrders, AffectedOrdersRequest>({
      query: ({ clientId, contact }) => ({
        url: `/timber/api/internal/clients/${clientId}/default-contact-impact`,
        method: 'POST',
        body: {
          adminId: contact.adminId,
          statusLowerRange: contact.statusLowerRange,
          statusUpperRange: contact.statusUpperRange || contact.statusLowerRange,
          roles: contact.roles,
          operation: contact.updateType,
        } as ICalculateImpactRequest,
      }),
      transformResponse: (response: ICalculateImpactResponse) => ({
        affectedOrders: response.affectedOrdersCount,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  // Client Users
  useListClientUsersQuery,
  useAddClientUserMutation,
  useEditClientContactsMutation,
  useActionClientContactsMutation,

  // Default Contacts
  useListDefaultContactsQuery,
  useAddDefaultContactMutation,
  useUpdateDefaultContactMutation,
  useDeleteDefaultContactMutation,
  useAffectedOrdersForDefaultContactMutation,
} = ClientUsersAPI;
